import { sortBy } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    componentReaction,
    Enums,
    errorRedirect,
    FormStore,
    inject,
    Input,
    Loader,
    Modal,
    ModalHandle,
    Nullable,
    SelectInput,
    TasksStore,
    toArray,
    toKeyArray,
    upperCaseEachWord,
    uuidv4generate,
} from 'Mri.Web.Common';
import * as React from 'react';
import { Button, Form, FormGroup } from 'reactstrap';
import { AccountsStore } from '../Accounts/AccountsStore';
import { ContractsStore } from '../Contracts/ContractsStore';
import { ProductionProjectsStore } from './ProductionProjectsStore';

interface ISelectOption {
    value: string;
    label: string;
}

export interface ICreateOrEditProductionProjectProps {
    readonly guid?: string;
    readonly modalHandle: ModalHandle;
    readonly viewState: 'create' | 'edit';
    readonly history: any;
}

@autoinject
@observer
export class CreateOrEditProductionProject extends React.Component<ICreateOrEditProductionProjectProps> {
    public render() {
        return (
            <Modal
                title={(this.props.viewState == 'edit' ? 'Edit' : 'Create') + ' Production Project'}
                modalHandle={this.props.modalHandle}
                size="lg"
                className="my-2"
                footerClass="d-none"
                onOpened={this.onOpenedAsync}
            >
                <div className="container-fluid pt-3">
                    {this.productionProjectForm && (
                        <Form onSubmit={this.submitAsync}>
                            {!!this.productionProjectForm.globalErrors.length && (
                                <div className="alert-danger p-2">
                                    <ul>
                                        {this.productionProjectForm.globalErrors.map((e: string, idx: number) => (
                                            <li key={idx}>{e}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <div>
                                <FormGroup color={this.productionProjectForm.fields.name.state}>
                                    <label>
                                        <strong>Name</strong>
                                    </label>

                                    <Input
                                        type="text"
                                        field={this.productionProjectForm.fields.name}
                                        placeholder="Name"
                                        required
                                    />
                                </FormGroup>

                                <FormGroup color={this.productionProjectForm.fields.comments.state}>
                                    <label>
                                        <strong>Comments</strong>
                                    </label>

                                    <Input
                                        type="text"
                                        field={this.productionProjectForm.fields.comments}
                                        placeholder="Comments"
                                    />
                                </FormGroup>

                                <FormGroup color={this.productionProjectForm.fields.comments.state}>
                                    <label>
                                        <strong>Account</strong>
                                    </label>

                                    <SelectInput
                                        field={this.productionProjectForm.fields.accountNumber}
                                        options={this.accountOptions}
                                        placeholder="Account Number"
                                    />
                                </FormGroup>

                                <FormGroup color={this.productionProjectForm.fields.comments.state}>
                                    <label>
                                        <strong>Intended Contract</strong>
                                    </label>

                                    <SelectInput
                                        field={this.productionProjectForm.fields.intendedContractNumber}
                                        options={this.contractOptions}
                                        placeholder="Contract"
                                    />
                                </FormGroup>

                                {this.props.viewState == 'edit' && (
                                    <div className="mb-3">
                                        <label>
                                            <strong>Model Definitions</strong>
                                        </label>

                                        <Button color="primary" className="ms-3" onClick={this.addRemoveModels}>
                                            Add/Remove Models
                                        </Button>

                                        {this.projectModels.map(m => (
                                            <div key={m.modelDefinitionGuid} className="align-middle">
                                                - {m.modelNumber}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <FormGroup color={this.productionProjectForm.fields.projectStatus.state}>
                                    <label>
                                        <strong>Project Status</strong>
                                    </label>

                                    <Input
                                        type="select"
                                        field={this.productionProjectForm.fields.projectStatus}
                                        required
                                    >
                                        <option value="" disabled>
                                            -
                                        </option>
                                        {toKeyArray(Enums.ProjectStatus).map(key => (
                                            <option value={key} key={key}>
                                                {Enums.toString(Enums.ProjectStatus, key)}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>

                                <FormGroup>
                                    <Button type="submit" color="primary">
                                        <Loader loading={this.loading} showSpinner={true} message="Submitting..." />
                                        {!this.loading && (
                                            <span>
                                                {this.props.viewState == 'create' && 'Create Project'}
                                                {this.props.viewState == 'edit' && 'Update Project'}
                                            </span>
                                        )}
                                    </Button>
                                </FormGroup>
                            </div>
                        </Form>
                    )}
                </div>
                <Loader
                    loading={this.loading}
                    message=" Loading Project..."
                    showSpinner
                    className="w-100 text-center"
                />
            </Modal>
        );
    }

    @bind
    @action
    private async addRemoveModels() {
        this.props.history.push(
            `/production-projects/${this.productionProject?.productionProjectGuid}/${this.productionProject?.name}`
        );
    }

    @bind
    @action
    private async onOpenedAsync() {
        this.loading = true;
        try {
            this.fillFormAsync();
        } catch (e: any) {
            errorRedirect(e);
        } finally {
            this.loading = false;
        }
    }

    @computed
    private get productionProject() {
        if (this.props.guid) {
            return this.productionProjectsStore.productionProjects.get(this.props.guid);
        }
    }

    @action
    private async fillFormAsync() {
        if (this.props.viewState == 'edit' && !this.productionProject) throw new Error('Project does not exist');
        await this.accountsStore.loadAllAsync();
        await this.contractsStore.loadAllAsync();
        if (this.productionProject) {
            if (this.props.viewState == 'create') {
                this.productionProjectForm = new FormStore<Mri.ProductionProjects.ProductionProject>({
                    ...this.productionProject,
                    name: '',
                    productionProjectGuid: uuidv4generate(),
                });
            } else {
                this.productionProjectForm = new FormStore<Mri.ProductionProjects.ProductionProject>({
                    ...this.productionProject,
                });
                await this.productionProjectsStore.loadModelsForProject(this.productionProject.productionProjectGuid);
            }
        } else {
            const model: Nullable<Mri.ProductionProjects.ProductionProject> = {
                name: '',
                comments: '',
                accountNumber: this.productionProjectForm?.fields.accountNumber.value || '',
                intendedContractNumber: this.productionProjectForm?.fields.intendedContractNumber.value || '',
                productionProjectGuid: uuidv4generate(),
                projectStatus:
                    this.productionProjectForm?.fields.projectStatus.value ||
                    Mri.ProductionProjects.ProjectStatus.Active,
            };

            this.productionProjectForm = new FormStore<Mri.ProductionProjects.ProductionProject>(model);
        }

        componentReaction(this, () => this.productionProjectForm!.fields.accountNumber.value, this.clearContract);
    }

    @bind
    @action
    private clearContract() {
        if (!this.productionProjectForm) throw new Error('Form does not exist');

        this.productionProjectForm.fields.intendedContractNumber.clear();
    }

    @bind
    @action
    private async submitAsync(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (!this.productionProjectForm) throw new Error('Form does not exist');

        this.productionProjectForm.onSubmit();
        if (this.productionProjectForm.validate()) {
            this.loading = true;
            const task = this.tasksStore.startSessionTask(upperCaseEachWord(this.props.viewState) + ' Config');

            try {
                if (this.props.viewState == 'edit' && this.props.guid) {
                    await this.productionProjectsStore.updateAsync(this.props.guid, this.productionProjectForm.values);
                } else if (this.props.viewState == 'create') {
                    await this.productionProjectsStore.createAsync(this.productionProjectForm.values);
                    const project = this.productionProjectsStore.productionProjects.get(
                        this.productionProjectForm.fields.productionProjectGuid.value
                    );
                    if (project) {
                        this.props.history.push(
                            `/production-projects/${project?.productionProjectGuid}/${project.name}`
                        );
                    }
                }
                task.complete();
            } catch (e: any) {
                if (e.modelState) {
                    this.productionProjectForm.clearGlobalErrors();
                    this.productionProjectForm.addErrors(e.modelState);
                }

                task.error(e);
            } finally {
                this.loading = false;
            }
        } else {
            console.log('invalid');
            this.loading = false;
        }

        this.props.modalHandle.close();
    }

    @computed
    private get accountOptions(): ISelectOption[] {
        let accounts = toArray(this.accountsStore.accounts).map(a => {
            return {
                value: a.accountNumber,
                label: a.accountName,
            };
        });

        accounts = sortBy(accounts, a => a.label);
        return accounts;
    }

    @computed
    private get contractOptions(): ISelectOption[] {
        if (this.productionProjectForm?.fields.accountNumber.value) {
            return (
                this.contractsStore.accountContracts
                    .get(this.productionProjectForm?.fields.accountNumber.value)
                    ?.map(c => {
                        return {
                            value: c.serviceCO,
                            label: c.serviceCO + ' ' + c.contractNumber,
                        };
                    }) ?? []
            );
        }
        return [];
    }

    @bind
    @action
    private getContractDefaultValue(serviceCO: string) {
        const contract = this.contractsStore.contracts.get(serviceCO);
        if (contract) {
            return {
                value: contract.serviceCO,
                label: contract.serviceCO + ' ' + contract.contractNumber,
            };
        }
    }

    @computed
    private get projectModels() {
        const models =
            this.props.guid && this.productionProjectsStore.models.has(this.props.guid)
                ? toArray(this.productionProjectsStore.models.get(this.props.guid)!)
                : [];
        return models;
    }

    @observable private productionProjectForm?: FormStore<Mri.ProductionProjects.ProductionProject>;
    @observable private loading = false;
    @inject private accountsStore!: AccountsStore;
    @inject private readonly productionProjectsStore!: ProductionProjectsStore;
    @inject private readonly contractsStore!: ContractsStore;
    @inject private tasksStore!: TasksStore;
}
